// src/components/UI/NotificationContainer.tsx
// Toast-notifikationer för SireusCode

import React, { useEffect } from 'react';
import { X, CheckCircle, XCircle, AlertTriangle, Info } from 'lucide-react';
import { useAppContext } from '../../contexts/AppContext';
import { Notification } from '../../types';

interface NotificationItemProps {
  notification: Notification;
  onClose: (id: string) => void;
}

const NotificationItem: React.FC<NotificationItemProps> = ({ notification, onClose }) => {
  const getIcon = () => {
    switch (notification.type) {
      case 'success':
        return <CheckCircle className="w-5 h-5" />;
      case 'error':
        return <XCircle className="w-5 h-5" />;
      case 'warning':
        return <AlertTriangle className="w-5 h-5" />;
      case 'info':
      default:
        return <Info className="w-5 h-5" />;
    }
  };

  const getStyles = () => {
    const baseStyles = "flex items-start gap-3 p-4 rounded-lg shadow-lg max-w-sm transition-all duration-300 ease-in-out";
    
    switch (notification.type) {
      case 'success':
        return `${baseStyles} bg-green-50 text-green-800 border-l-4 border-green-400`;
      case 'error':
        return `${baseStyles} bg-red-50 text-red-800 border-l-4 border-red-400`;
      case 'warning':
        return `${baseStyles} bg-yellow-50 text-yellow-800 border-l-4 border-yellow-400`;
      case 'info':
      default:
        return `${baseStyles} bg-blue-50 text-blue-800 border-l-4 border-blue-400`;
    }
  };

  return (
    <div className={getStyles()}>
      <div className="flex-shrink-0">
        {getIcon()}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium leading-5">
          {notification.message}
        </p>
        {notification.timestamp && (
          <p className="text-xs opacity-75 mt-1">
            {new Date(notification.timestamp).toLocaleTimeString()}
          </p>
        )}
      </div>
      <button
        onClick={() => onClose(notification.id)}
        className="flex-shrink-0 ml-2 p-1 rounded-full hover:bg-black hover:bg-opacity-10 transition-colors"
        aria-label="Stäng notifikation"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

const NotificationContainer: React.FC = () => {
  const { state, dispatch } = useAppContext();

  // Handle keyboard events for accessibility
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && state.notifications.length > 0) {
        dispatch({ type: 'CLEAR_NOTIFICATIONS' });
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [state.notifications.length, dispatch]);

  const handleClose = (id: string) => {
    dispatch({ type: 'REMOVE_NOTIFICATION', payload: id });
  };

  if (state.notifications.length === 0) return null;

  return (
    <>
      {/* Backdrop for mobile - optional */}
      {state.notifications.length > 0 && (
        <div className="fixed inset-0 pointer-events-none z-40" />
      )}
      
      {/* Notification container */}
      <div 
        className="fixed top-4 right-4 z-50 space-y-3 max-h-screen overflow-y-auto"
        role="region"
        aria-label="Notifikationer"
        aria-live="polite"
      >
        {state.notifications.map((notification, index) => (
          <div
            key={notification.id}
            className="animate-in slide-in-from-right duration-300"
            style={{ 
              animationDelay: `${index * 100}ms`,
              animationFillMode: 'both'
            }}
          >
            <NotificationItem 
              notification={notification} 
              onClose={handleClose}
            />
          </div>
        ))}
        
        {/* Clear all button when multiple notifications */}
        {state.notifications.length > 1 && (
          <div className="pt-2">
            <button
              onClick={() => dispatch({ type: 'CLEAR_NOTIFICATIONS' })}
              className="w-full text-xs text-gray-500 hover:text-gray-700 underline text-center py-2"
            >
              Rensa alla notifikationer
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default NotificationContainer;